import React, { useState, useEffect } from 'react';
import {
  BarChart3,
  TrendingUp,
  MousePointer,
  Users,
  Globe,
  Calendar,
  Filter,
  Download,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  User,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  AdsService,
  AnalyticsSummary,
  Ad,
  ClicksByUser,
} from '../services/ads';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

interface AnalyticsCardProps {
  title: string;
  value: string | number;
  change?: string;
  icon: React.ReactNode;
  color: 'blue' | 'green' | 'purple' | 'orange';
}

const AnalyticsCard: React.FC<AnalyticsCardProps> = ({
  title,
  value,
  change,
  icon,
  color,
}) => {
  const colorClasses = {
    blue: 'text-blue-500 bg-blue-50',
    green: 'text-green-500 bg-green-50',
    purple: 'text-purple-500 bg-purple-50',
    orange: 'text-orange-500 bg-orange-50',
  };

  return (
    <Card className='card-enhanced hover-lift'>
      <CardContent className='p-6'>
        <div className='flex items-center justify-between'>
          <div>
            <p className='text-sm font-medium text-gray-600'>{title}</p>
            <p className='text-2xl font-bold text-gray-900 mt-2'>{value}</p>
            {change && (
              <p className='text-sm text-green-600 mt-1'>
                <TrendingUp className='inline h-4 w-4 mr-1' />
                {change}
              </p>
            )}
          </div>
          <div className={`p-3 rounded-full ${colorClasses[color]}`}>
            {icon}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

interface ChartPlaceholderProps {
  title: string;
  type: 'line' | 'bar' | 'pie' | 'area';
  height?: number;
  data?: any[];
}

const ChartPlaceholder: React.FC<ChartPlaceholderProps> = ({
  title,
  type,
  height = 300,
  data = [],
}) => {
  return (
    <Card className='card-enhanced'>
      <CardHeader>
        <CardTitle className='text-lg font-semibold text-gray-900 flex items-center'>
          <BarChart3 className='h-5 w-5 mr-2' />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div
          className='bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg flex items-center justify-center border-2 border-dashed border-gray-300'
          style={{ height: `${height}px` }}
        >
          <div className='text-center'>
            <BarChart3 className='h-12 w-12 text-gray-400 mx-auto mb-3' />
            <p className='text-gray-500 font-medium'>{title}</p>
            <p className='text-sm text-gray-400 mt-1'>
              {type.charAt(0).toUpperCase() + type.slice(1)} Chart
            </p>
            {data.length > 0 && (
              <p className='text-xs text-gray-400 mt-2'>
                {data.length} data points
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export const AdAnalyticsDashboard: React.FC = () => {
  const [summary, setSummary] = useState<AnalyticsSummary | null>(null);
  const [topAds, setTopAds] = useState<Ad[]>([]);
  const [clicksByDate, setClicksByDate] = useState<
    { date: string; clicks: number }[]
  >([]);
  const [clicksByUser, setClicksByUser] = useState<ClicksByUser | null>(null);
  const [expandedAds, setExpandedAds] = useState<Record<string, boolean>>({});
  const [clicksByUserLoading, setClicksByUserLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d'>('30d');
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadDashboardData();
  }, [timeRange]);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      // Convert timeRange to date range
      const endDate = new Date();
      const startDate = new Date();
      const days = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90;
      startDate.setDate(endDate.getDate() - days);

      // Load ads with date filters to ensure consistency across all data
      const dateFilters = {
        startDate: startDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0],
      };

      const adsData = await AdsService.getAds(dateFilters);
      const activeAds = adsData.filter(ad => ad.active);

      // If we have active ads, get analytics summary
      if (activeAds.length > 0) {
        const query = {
          adIds: activeAds.map(ad => ad.id),
          startDate: startDate.toISOString().split('T')[0],
          endDate: endDate.toISOString().split('T')[0],
        };

        // Load summary analytics
        const summaryData = await AdsService.getAnalyticsSummary(query);
        setSummary(summaryData);

        const queryParams = {
          startDate: startDate.toISOString().split('T')[0],
          endDate: endDate.toISOString().split('T')[0],
        };

        console.log(
          'Fetching tracking analytics for all active ads:',
          activeAds.length
        );

        try {
          const analyticsPromises = activeAds.map(ad => {
            console.log(`Fetching analytics for ad: ${ad.id} - ${ad.name}`);
            return AdsService.getTrackingAnalytics(ad.id, queryParams).catch(
              error => {
                console.error(
                  `Error fetching analytics for ad ${ad.id}:`,
                  error
                );
                // Devuelve un objeto vacío para que Promise.all continúe con otros anuncios
                return {
                  adId: ad.id,
                  adName: ad.name,
                  totalClicks: 0,
                  uniqueUsers: 0,
                  clicksByDate: [],
                  clicksByCountry: [],
                  clicksByDevice: [],
                  clicksByBrowser: [],
                  topReferrers: [],
                  recentEvents: [],
                };
              }
            );
          });

          const allAnalyticsData = await Promise.all(analyticsPromises);
          console.log(
            'Received tracking analytics data for all ads:',
            allAnalyticsData.length
          );

          // Combinar todos los datos de clics por fecha en un solo conjunto
          const combinedClicksByDate = new Map<string, number>();

          // Procesar los datos de cada anuncio
          let totalDataPoints = 0;
          allAnalyticsData.forEach(adData => {
            if (
              adData &&
              adData.clicksByDate &&
              adData.clicksByDate.length > 0
            ) {
              totalDataPoints += adData.clicksByDate.length;
              adData.clicksByDate.forEach(datePoint => {
                if (datePoint && datePoint.date) {
                  // Asegurar que la fecha esté en formato YYYY-MM-DD
                  const date = datePoint.date.split('T')[0];
                  const clicks = datePoint.clicks || 0;
                  const currentClicks = combinedClicksByDate.get(date) || 0;
                  combinedClicksByDate.set(date, currentClicks + clicks);
                }
              });
            }
          });

          console.log(`Total data points processed: ${totalDataPoints}`);

          // Convertir el Map a un array para su uso en el gráfico
          const combinedClicksArray = Array.from(combinedClicksByDate.entries())
            .map(([date, clicks]) => ({ date, clicks }))
            .sort((a, b) => {
              // Asegurar formato YYYY-MM-DD para evitar problemas de zona horaria
              const dateA = a.date.split('T')[0];
              const dateB = b.date.split('T')[0];
              return dateA.localeCompare(dateB);
            });

          console.log(
            'Combined clicks by date for all ads:',
            combinedClicksArray.length
          );

          if (combinedClicksArray.length > 0) {
            console.log('First date point:', combinedClicksArray[0]);
            console.log(
              'Last date point:',
              combinedClicksArray[combinedClicksArray.length - 1]
            );
          }

          setClicksByDate(combinedClicksArray);
        } catch (error) {
          console.error(
            'Failed to load tracking analytics for all ads:',
            error
          );
          setClicksByDate([]);
        }
      } else {
        // No active ads, set empty summary
        setSummary({
          summary: {
            totalAds: 0,
            totalClicks: 0,
            totalUniqueUsers: 0,
            averageClicksPerAd: 0,
          },
          ads: [],
        });
        setClicksByDate([]);
      }

      // Load top performing ads
      const sortedAds = adsData
        .filter(ad => ad.clickCount && ad.clickCount > 0)
        .sort((a, b) => (b.clickCount || 0) - (a.clickCount || 0))
        .slice(0, 10);
      setTopAds(sortedAds);

      // Load clicks by user data
      if (activeAds.length > 0) {
        setClicksByUserLoading(true);
        try {
          const clicksByUserQuery = {
            adIds: activeAds.map(ad => ad.id),
            startDate: startDate.toISOString().split('T')[0],
            endDate: endDate.toISOString().split('T')[0],
          };

          console.log(
            'Fetching clicks by user data for ads:',
            activeAds.length
          );
          const clicksByUserData =
            await AdsService.getClicksByUser(clicksByUserQuery);
          console.log('Received clicks by user data:', clicksByUserData);
          setClicksByUser(clicksByUserData);
        } catch (error) {
          console.error('Failed to load clicks by user data:', error);
          setClicksByUser(null);
        } finally {
          setClicksByUserLoading(false);
        }
      } else {
        setClicksByUser(null);
        setClicksByUserLoading(false);
      }
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadDashboardData();
    setRefreshing(false);
  };

  const toggleAdExpansion = (adId: string) => {
    setExpandedAds(prev => ({
      ...prev,
      [adId]: !prev[adId],
    }));
  };

  const formatNumber = (value: number) => {
    return value.toLocaleString();
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  // Utility function to generate dummy time series data for testing
  const generateDummyTimeSeriesData = (startDate: Date, endDate: Date) => {
    const dummyData: { date: string; clicks: number }[] = [];
    const currentDate = new Date(startDate);

    while (currentDate <= endDate) {
      // Generate random click count between 10 and 100
      const clicks = Math.floor(Math.random() * 90) + 10;

      // Asegurar formato YYYY-MM-DD
      const year = currentDate.getFullYear();
      const month = String(currentDate.getMonth() + 1).padStart(2, '0');
      const day = String(currentDate.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;

      dummyData.push({
        date: dateStr,
        clicks: clicks,
      });

      // Move to next day
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return dummyData;
  };

  if (loading) {
    return (
      <div className='space-y-6'>
        {/* Loading Skeleton */}
        <div className='flex items-center justify-between'>
          <div>
            <div className='h-8 w-64 bg-gray-200 rounded animate-pulse'></div>
            <div className='h-4 w-96 bg-gray-200 rounded animate-pulse mt-2'></div>
          </div>
          <div className='h-10 w-32 bg-gray-200 rounded animate-pulse'></div>
        </div>

        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6'>
          {[...Array(4)].map((_, i) => (
            <div
              key={i}
              className='h-32 bg-gray-200 rounded-lg animate-pulse'
            ></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4'>
        <div>
          <h1 className='text-2xl font-bold text-gray-900'>Traffic Insights</h1>
          <p className='text-gray-600'>
            Comprehensive insights into your ad performance and engagement
            metrics
          </p>
        </div>

        <div className='flex items-center space-x-3'>
          {/* Time Range Selector */}
          <div className='flex space-x-1 bg-gray-100 rounded-lg p-1'>
            {(['7d', '30d', '90d'] as const).map(range => (
              <button
                key={range}
                onClick={() => setTimeRange(range)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  timeRange === range
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {range === '7d'
                  ? '7 days'
                  : range === '30d'
                    ? '30 days'
                    : '90 days'}
              </button>
            ))}
          </div>

          {/* Actions */}
          <Button
            onClick={handleRefresh}
            disabled={refreshing}
            className='btn-enhanced btn-secondary'
          >
            <RefreshCw
              className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`}
            />
            Refresh
          </Button>

          {/* <Button className='btn-enhanced btn-primary'>
            <Download className='h-4 w-4 mr-2' />
            Export
          </Button> */}
        </div>
      </div>

      {/* Key Metrics */}
      {summary && (
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6'>
          <AnalyticsCard
            title='Total Links'
            value={formatNumber(summary.summary.totalAds)}
            icon={<BarChart3 className='h-6 w-6' />}
            color='blue'
          />
          <AnalyticsCard
            title='Total Clicks'
            value={formatNumber(summary.summary.totalClicks)}
            change=''
            icon={<MousePointer className='h-6 w-6' />}
            color='green'
          />
          <AnalyticsCard
            title='Users'
            value={formatNumber(summary.summary.totalUniqueUsers)}
            change=''
            icon={<Users className='h-6 w-6' />}
            color='purple'
          />
          <AnalyticsCard
            title='Avg. Clicks/Link'
            value={formatNumber(Math.round(summary.summary.averageClicksPerAd))}
            change=''
            icon={<TrendingUp className='h-6 w-6' />}
            color='orange'
          />
        </div>
      )}

      {/* Charts Section */}
      <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
        <Card className='card-enhanced'>
          <CardHeader>
            <CardTitle className='text-lg font-semibold text-gray-900 flex items-center'>
              <BarChart3 className='h-5 w-5 mr-2' />
              Clicks Over Time
            </CardTitle>
          </CardHeader>
          <CardContent>
            {clicksByDate && clicksByDate.length > 0 ? (
              <ResponsiveContainer width='100%' height={350}>
                <LineChart
                  data={clicksByDate}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray='3 3' />
                  <XAxis
                    dataKey='date'
                    tickFormatter={date => {
                      try {
                        // No convertir a Date para evitar problemas de zona horaria
                        // Solo formatear la parte de fecha en formato YYYY-MM-DD
                        const parts = date.split('-');
                        if (parts.length === 3) {
                          // Formato más amigable: DD/MM/YYYY
                          return `${parts[2]}/${parts[1]}/${parts[0]}`;
                        }
                        return date;
                      } catch (e) {
                        console.error('Error formatting date:', date, e);
                        return date;
                      }
                    }}
                  />
                  <YAxis />
                  <Tooltip
                    labelFormatter={date => {
                      try {
                        // Procesar manualmente el formato YYYY-MM-DD para evitar problemas de zona horaria
                        const parts = date.split('-');
                        if (parts.length === 3) {
                          const year = parts[0];
                          const month = parseInt(parts[1], 10) - 1; // JavaScript months are 0-indexed
                          const day = parseInt(parts[2], 10);

                          // Usar Intl.DateTimeFormat para localización
                          return new Intl.DateTimeFormat('es-ES', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          }).format(new Date(year, month, day));
                        }
                        return date;
                      } catch (e) {
                        console.error(
                          'Error formatting tooltip date:',
                          date,
                          e
                        );
                        return date;
                      }
                    }}
                  />
                  <Legend />
                  <Line
                    type='monotone'
                    dataKey='clicks'
                    stroke='#3b82f6'
                    activeDot={{ r: 8 }}
                    name='Clicks'
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div
                className='bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg flex items-center justify-center border-2 border-dashed border-gray-300'
                style={{ height: '350px' }}
              >
                <div className='text-center'>
                  <BarChart3 className='h-12 w-12 text-gray-400 mx-auto mb-3' />
                  <p className='text-gray-500 font-medium'>Clicks Over Time</p>
                  <p className='text-sm text-gray-400 mt-1'>
                    No data available
                  </p>
                  {/* <Button
                    className='mt-4 btn-enhanced btn-secondary'
                    onClick={() => {
                      const endDate = new Date();
                      const startDate = new Date();
                      const days =
                        timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90;
                      startDate.setDate(endDate.getDate() - days);
                      setClicksByDate(
                        generateDummyTimeSeriesData(startDate, endDate)
                      );
                    }}
                  >
                    Simular Datos
                  </Button> */}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className='card-enhanced'>
          <CardHeader>
            <CardTitle className='text-lg font-semibold text-gray-900 flex items-center'>
              <BarChart3 className='h-5 w-5 mr-2' />
              Performance by Link
            </CardTitle>
          </CardHeader>
          <CardContent>
            {topAds && topAds.length > 0 ? (
              <ResponsiveContainer width='100%' height={350}>
                <BarChart
                  data={topAds.map(ad => ({
                    name: ad.name,
                    clicks: ad.clickCount || 0,
                    users: ad.uniqueUsersCount || 0,
                  }))}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray='3 3' />
                  <XAxis dataKey='name' />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey='clicks' fill='#3b82f6' name='Total Clicks' />
                  <Bar dataKey='users' fill='#8b5cf6' name='Users' />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div
                className='bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg flex items-center justify-center border-2 border-dashed border-gray-300'
                style={{ height: '350px' }}
              >
                <div className='text-center'>
                  <BarChart3 className='h-12 w-12 text-gray-400 mx-auto mb-3' />
                  <p className='text-gray-500 font-medium'>Performance by Ad</p>
                  <p className='text-sm text-gray-400 mt-1'>
                    No data available
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Top Performing Ads */}
      <Card className='card-enhanced'>
        <CardHeader>
          <CardTitle className='text-lg font-semibold text-gray-900 flex items-center'>
            <TrendingUp className='h-5 w-5 mr-2' />
            Top Performing Ads
          </CardTitle>
        </CardHeader>
        <CardContent>
          {topAds.length > 0 ? (
            <div className='space-y-4'>
              {topAds.map((ad, index) => (
                <div
                  key={ad.id}
                  className='flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors'
                >
                  <div className='flex items-center space-x-4'>
                    <div className='flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-600 rounded-full text-sm font-semibold'>
                      {index + 1}
                    </div>
                    <div>
                      <h4 className='font-medium text-gray-900'>{ad.name}</h4>
                      <p className='text-sm text-gray-600 truncate max-w-md'>
                        {ad.description || ad.destinationUrl}
                      </p>
                    </div>
                  </div>
                  <div className='flex items-center space-x-4'>
                    <div className='text-right'>
                      <p className='text-sm font-medium text-gray-900'>
                        {formatNumber(ad.clickCount || 0)} clicks
                      </p>
                      <p className='text-xs text-gray-500'>
                        {formatNumber(ad.uniqueUsersCount || 0)} unique users
                      </p>
                    </div>
                    <Badge
                      className={
                        ad.userTrackingEnabled
                          ? 'badge-success'
                          : 'badge-secondary'
                      }
                    >
                      {ad.userTrackingEnabled ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className='text-center py-8'>
              <BarChart3 className='h-12 w-12 text-gray-400 mx-auto mb-3' />
              <p className='text-gray-500'>No performance data available</p>
              <p className='text-sm text-gray-400 mt-1'>
                Create some ads and start tracking to see performance metrics
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Clicks by User */}
      <Card className='card-enhanced'>
        <CardHeader>
          <CardTitle className='text-lg font-semibold text-gray-900 flex items-center'>
            <User className='h-5 w-5 mr-2' />
            Clicks by User
          </CardTitle>
        </CardHeader>
        <CardContent>
          {clicksByUserLoading ? (
            <div className='space-y-4'>
              {[...Array(3)].map((_, i) => (
                <div key={i} className='border border-gray-200 rounded-lg p-4'>
                  <div className='flex items-center justify-between'>
                    <div className='flex items-center space-x-3'>
                      <div className='h-4 w-4 bg-gray-200 rounded animate-pulse'></div>
                      <div className='h-4 w-32 bg-gray-200 rounded animate-pulse'></div>
                    </div>
                    <div className='flex space-x-2'>
                      <div className='h-6 w-16 bg-gray-200 rounded animate-pulse'></div>
                      <div className='h-6 w-20 bg-gray-200 rounded animate-pulse'></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : clicksByUser &&
            clicksByUser.data &&
            clicksByUser.data.length > 0 ? (
            <div className='space-y-4'>
              {clicksByUser.data.map(adData => (
                <div
                  key={adData.adId}
                  className='border border-gray-200 rounded-lg'
                >
                  <button
                    onClick={() => toggleAdExpansion(adData.adId)}
                    className='w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
                  >
                    <div className='flex items-center space-x-3'>
                      <div className='flex items-center space-x-2'>
                        {expandedAds[adData.adId] ? (
                          <ChevronDown className='h-4 w-4 text-gray-500' />
                        ) : (
                          <ChevronRight className='h-4 w-4 text-gray-500' />
                        )}
                        <h4 className='font-medium text-gray-900 text-left'>
                          {adData.adName}
                        </h4>
                      </div>
                    </div>
                    <div className='flex items-center space-x-4'>
                      <Badge className='badge-secondary'>
                        {adData.users.length} users
                      </Badge>
                      <Badge className='badge-primary'>
                        {formatNumber(adData.totalClicks)} total clicks
                      </Badge>
                    </div>
                  </button>

                  {expandedAds[adData.adId] && (
                    <div className='border-t border-gray-200 bg-white'>
                      {adData.users.length > 0 ? (
                        <div className='p-4 space-y-3'>
                          {adData.users.map((user, userIndex) => (
                            <div
                              key={`${user.userId || 'anonymous'}-${userIndex}`}
                              className='flex items-center justify-between p-3 bg-gray-50 rounded-lg'
                            >
                              <div className='flex items-center space-x-3'>
                                <div className='flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-600 rounded-full'>
                                  <User className='h-4 w-4' />
                                </div>
                                <div>
                                  <p className='font-medium text-gray-900'>
                                    {user.displayName}
                                  </p>
                                  {user.email && (
                                    <p className='text-sm text-gray-500'>
                                      {user.email}
                                    </p>
                                  )}
                                </div>
                              </div>
                              <div className='text-right'>
                                <p className='text-sm font-medium text-gray-900'>
                                  {formatNumber(user.clickCount)} clicks
                                </p>
                                {user.userId && (
                                  <p className='text-xs text-gray-500'>
                                    ID: {user.userId}
                                  </p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className='p-4 text-center text-gray-500'>
                          <User className='h-8 w-8 mx-auto mb-2 text-gray-400' />
                          <p>No user click data available for this ad</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className='text-center py-8'>
              <User className='h-12 w-12 text-gray-400 mx-auto mb-3' />
              <p className='text-gray-500'>No user click data available</p>
              <p className='text-sm text-gray-400 mt-1'>
                Click data will appear here once users interact with your ads
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Activity */}
      {/* <Card className='card-enhanced'>
        <CardHeader>
          <CardTitle className='text-lg font-semibold text-gray-900 flex items-center'>
            <Calendar className='h-5 w-5 mr-2' />
            Recent Activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className='space-y-3'>
            {[
              {
                action: 'New ad created',
                ad: 'Summer Campaign 2024',
                time: '2 hours ago',
              },
              {
                action: 'Click tracked',
                ad: 'Product Launch',
                time: '4 hours ago',
              },
              {
                action: 'Ad updated',
                ad: 'Newsletter Signup',
                time: '1 day ago',
              },
              {
                action: 'Tracking enabled',
                ad: 'Mobile App Promo',
                time: '2 days ago',
              },
              {
                action: 'Analytics exported',
                ad: 'Q3 Campaign',
                time: '3 days ago',
              },
            ].map((activity, index) => (
              <div key={index} className='flex items-center space-x-3 py-2'>
                <div className='w-2 h-2 bg-blue-500 rounded-full'></div>
                <div className='flex-1'>
                  <p className='text-sm text-gray-900'>
                    <span className='font-medium'>{activity.action}</span>
                    {' for "'}
                    <span className='text-blue-600'>{activity.ad}</span>
                    {'"'}
                  </p>
                  <p className='text-xs text-gray-500'>{activity.time}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card> */}
    </div>
  );
};
